<script setup lang="ts">
import { Separator } from 'radix-vue';
import FilterBar from '~/components/filter-bar/index.vue';
import Description from '~/components/plp/Description.vue';
import ProductList from '~/components/plp/ProductList.vue';
import SeoProvider from '~/components/plp/SeoProvider.vue';
import WebPageSeo from '~/components/seo/WebPageSeo.vue';
import { useBrandOfDrop } from '~/data/entities';
import {
	fetchBrandProducts,
	fetchDropProducts,
	fetchProductsCount,
	useProductsCount,
	useProductsList,
} from '~/data/products';

const { $cathodeClient } = useNuxtApp();
const { storeType } = useGlobalStore();
const route = useRoute();

const routeName = 'drops-handle';
const handle = route.params.handle as string;

const filters = useFilterParams(routeName);

const count = useProductsCount({
	routeName,
	queryKey: ['drops', handle],
	queryFn: () =>
		fetchProductsCount({
			drop_handle: handle!,
		}),
});

const { data: brand, suspense } = useBrandOfDrop(handle);

onServerPrefetch(async () => {
	await suspense();
});

const {
	data,
	isLoading,
	hasNextPage,
	fetchNextPage,
	isFetchingNextPage,
	error: dropProductsError,
} = useProductsList({
	routeName,
	queryKey: ['drop', handle],
	queryFn: ({ pageParam: cursor }) => {
		return fetchDropProducts(handle, cursor as string);
	},
});
const enableBrandsCall = computed(() => {
	return !!brand.value;
});

const {
	data: brandData,
	isLoading: isLoadingBrandData,
	hasNextPage: brandHasNextPage,
	fetchNextPage: brandFetchNextPage,
	isFetchingNextPage: brandIsFetchingNextPage,
	error: brandProductsError,
} = useProductsList({
	routeName,
	queryKey: ['brand', brand, filters],
	queryFn: ({ pageParam: cursor }) => {
		return fetchBrandProducts(
			brand.value![0]!.handle,
			cursor as string,
			filters.value,
			handle,
		);
	},
	enabledOverride: enableBrandsCall,
});

onMounted(async () => {
	$cathodeClient.queueEvent('page_load', {
		source: `${window.location.origin}${route.path}`,
		page: 'PLP',
		statusCode: 200,
		referrer: document.referrer,
		collection_handle: route.params.handle as string,
	});
});

watch(isLoading, (isLoading) => {
	if (!isLoading && dropProductsError.value) {
		throw createError({
			statusCode: 404,
			message: 'Failed to load products',
			data: {
				url: route.fullPath,
			},
			fatal: true,
		});
	}
});

watch(isLoadingBrandData, (isLoading) => {
	if (!isLoading && brandProductsError.value) {
		throw createError({
			statusCode: 404,
			message: 'Failed to load brand products on drops page',
			data: {
				url: route.fullPath,
			},
		});
	}
});
</script>
<template>
	<SeoProvider v-slot="{ seo }" :handle="handle" :route-name="routeName">
		<WebPageSeo
			:data="seo"
			:item-list-data="data?.pages"
			:handle="handle"
			:count="count"
		/>
		<div>
			<PlpHeader :data="seo" :items-count="count" />
			<ClientOnly>
				<ProductList
					:handle="handle"
					entity="drops"
					filters=""
					:infinite-loader="false"
					:fetch-next-page="fetchNextPage"
					:is-loading="isLoading"
					:data="data"
					:has-next-page="hasNextPage"
					:is-fetching-next-page="isFetchingNextPage"
					:scroll-threshold="0.2"
				/>
				<template
					v-if="!!brand && brand.length && brandData?.pages[0]?.products.length"
				>
					<h1 v-if="!!brand" class="px-3xl text-lg font-semibold">
						More from {{ brand[0]!.name }}
					</h1>
					<FilterBar
						:handle="brand[0]!.handle"
						:is-product-list-loading="isLoading"
						:seo-description="false"
						:drops-handle="handle"
					/>
					<ProductList
						:infinite-loader="true"
						:fetch-next-page="brandFetchNextPage"
						:is-loading="isLoadingBrandData"
						:data="brandData"
						:has-next-page="brandHasNextPage"
						:is-fetching-next-page="brandIsFetchingNextPage"
					/>
				</template>
			</ClientOnly>

			<div
				v-if="
					!!seo?.description &&
					!!seo?.title &&
					storeType === STORE_TYPE.MARKETPLACE
				"
				class="w-full"
			>
				<Separator class="seo-description-separator" />
				<Description :description="seo.description" :heading="seo.title" />
			</div>
		</div>
	</SeoProvider>
</template>

<style scoped>
.seo-description-separator {
	@apply !bg-gray-100 h-xl mt-5xl;
}
</style>
